import { pickOEmbedFields } from '@common/media-layer/utils/media-embed.utils'
import {
  ATTACHMENT_ACCEPTED_DOCUMENT_TYPES,
  ATTACHMENT_MAX_FILE_SIZE,
  COMPOSER_ACCEPTED_IMAGE_TYPES,
  COMPOSER_ACCEPTED_VIDEO_TYPES,
  VOICE_NOTE_ACCEPTED_AUDIO_TYPES,
} from '@common/media-layer/utils/constants'

import type { ValidationError } from '@common/shared-ui-layer/types/validation.types'
import { MessageStatus } from '../types/chats.enums'

import type {
  EditorFileItem,
  EditorGifMedia,
  EditorMediaItem,
  EditorValidationLimits,
  MediaDimensionsInput,
} from '../types/chat-message-editor.types'
import type { IChatAttachment } from '../types/conversation.types'

interface ValidationToastNotifier {
  showError: (payload: { title: string; description: string }) => void
  showWarning: (payload: { title: string; description: string }) => void
  showInfo: (payload: { title: string; description: string }) => void
}

/** Toast validation errors from drag-drop / attachment pickers (matches posts/comments). */
export const showChatMessageEditorValidationErrors = (
  errors: ValidationError[],
  t: (key: string) => string,
  notification: ValidationToastNotifier
) => {
  errors.forEach(error => {
    const message = error.field ? `${error.field}: ${error.message}` : error.message
    const titleKey =
      error.type === 'warning'
        ? 'chats.messageEditor.uploadWarning'
        : error.type === 'info'
          ? 'chats.messageEditor.uploadInfo'
          : 'chats.messageEditor.uploadError'
    const payload = { title: t(titleKey), description: message }
    if (error.type === 'warning') notification.showWarning(payload)
    else if (error.type === 'info') notification.showInfo(payload)
    else notification.showError(payload)
  })
}

export const mapImageForCarousel = (media: EditorMediaItem) => ({
  url: media.url,
  file: media.file,
  id: media.status === MessageStatus.COMPLETED ? media.licenseId : undefined,
  uploading: media.status === MessageStatus.UPLOADING,
  error: media.error,
  progress: media.progress,
  licenseId: media.licenseId,
})

export const mapVideoForCarousel = (media: EditorMediaItem) => ({
  url: media.url,
  file: media.file,
  id: media.isImported ? media.id : media.status === MessageStatus.COMPLETED ? media.licenseId : undefined,
  uploading: media.status === MessageStatus.UPLOADING,
  error: media.error,
  progress: media.progress,
  licenseId: media.licenseId,
})

export const mapGifForCarousel = (gif: EditorGifMedia) => ({
  url: gif.url,
  title: gif.title,
  id: gif.id,
  uploading: gif.uploading,
  error: gif.error,
  progress: gif.progress,
})

export const mapFileForCarousel = (file: EditorFileItem) => ({
  url: file.url,
  file: file.file,
  name: file.name,
  size: file.size,
  type: file.type,
  id: file.status === MessageStatus.COMPLETED ? file.serverId : undefined,
  uploading: file.status === MessageStatus.UPLOADING,
  error: file.error,
  progress: file.progress,
})

export const buildMediaAttachments = (mediaItems: EditorMediaItem[]): IChatAttachment[] =>
  mediaItems
    .filter(media => {
      if (media.status !== MessageStatus.COMPLETED) return false
      if (media.isImported) return true
      if (media.licenseId) return true
      return !media.url.startsWith('blob:')
    })
    .map(media => ({
      // Never send blob URLs to backend. For DRM uploads, fallback to licenseId if URL is not resolved yet.
      url: !media.url.startsWith('blob:') ? media.url : media.licenseId || media.url,
      _id: media._id,
      type: media.type,
      name: media.name,
      licenseId: media.isImported ? undefined : media.licenseId,
      size: media.size,
      width: media.width,
      height: media.height,
      ...(media.isImported && pickOEmbedFields(media)),
    }))

export const buildGifAttachments = (gifs: EditorGifMedia[], gifFallbackName: string): IChatAttachment[] =>
  gifs.map(gif => ({
    _id: gif.id || `gif-${Date.now()}`,
    type: 'gif' as const,
    url: gif.url,
    name: gif.title || gifFallbackName,
  }))

export const buildFileAttachments = (files: EditorFileItem[]): IChatAttachment[] =>
  files
    .filter(file => file.status === MessageStatus.COMPLETED)
    .map(file => ({
      _id: file._id,
      type: 'document' as const,
      url: file.serverUrl || file.url,
      name: file.name,
      size: file.size,
      mimeType: file.type,
    }))

export const getMediaItemByTypeAndIndex = (
  mediaItems: EditorMediaItem[],
  type: 'image' | 'video',
  index: number
): EditorMediaItem | undefined => mediaItems.filter(media => media.type === type)[index]

export const buildEditorValidations = ({ maxImages, maxVideos, maxFiles }: EditorValidationLimits) => ({
  mediaValidations: {
    imageValidations: {
      maxFiles: maxImages,
      maxFileSize: ATTACHMENT_MAX_FILE_SIZE,
      maxDimensions: { width: 40000, height: 40000 },
      acceptedTypes: [...COMPOSER_ACCEPTED_IMAGE_TYPES],
    },
    videoValidations: {
      maxFiles: maxVideos,
      maxFileSize: 500 * 1024 * 1024,
      maxDuration: 180,
      acceptedTypes: [...COMPOSER_ACCEPTED_VIDEO_TYPES],
    },
  },
  fileValidations: {
    maxFiles: maxFiles,
    maxFileSize: ATTACHMENT_MAX_FILE_SIZE,
    acceptedTypes: [...ATTACHMENT_ACCEPTED_DOCUMENT_TYPES],
  },
  /** Plus-menu "Audio" picker — clips route to the voice-send path. */
  audioValidations: {
    maxFileSize: ATTACHMENT_MAX_FILE_SIZE,
    acceptedTypes: [...VOICE_NOTE_ACCEPTED_AUDIO_TYPES],
  },
})

export const resolveMediaDimensions = async (file: MediaDimensionsInput) => {
  if (typeof window === 'undefined') {
    return {}
  }

  if (file.type === 'image') {
    return new Promise<{ width?: number; height?: number }>(resolve => {
      const img = new Image()
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight })
      }
      img.onerror = () => resolve({})
      img.src = file.url
    })
  }

  if (file.type === 'video') {
    return new Promise<{ width?: number; height?: number }>(resolve => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.muted = true
      video.onloadedmetadata = () => {
        resolve({ width: video.videoWidth, height: video.videoHeight })
      }
      video.onerror = () => resolve({})
      video.src = file.url
    })
  }

  return {}
}
